import type { UnreadSurfaceKind } from "./unread-telemetry.js";

// A reseeded unread key names a human only with evidence: its live increment was a human DM, or a
// human's DM alert names it. A deleted cybo's stuck count never re-reads the members (#3812).
export function humanDmCandidates(
  keys: readonly string[],
  surfaceOf: (key: string) => UnreadSurfaceKind | undefined,
  humanDmActors: ReadonlySet<string>,
): string[] {
  return keys.filter((key) => surfaceOf(key) === "dm_human" || humanDmActors.has(key));
}

// One member re-read in flight per workspace; a trigger during it asks for exactly one more pass.
export class MemberRereadRunner {
  readonly #runs = new Map<string, { again: boolean }>();

  constructor(private readonly read: (workspaceId: string) => Promise<void>) {}

  request(workspaceId: string): void {
    const running = this.#runs.get(workspaceId);
    if (running) {
      running.again = true;
      return;
    }
    const run = { again: false };
    this.#runs.set(workspaceId, run);
    void this.#drain(workspaceId, run);
  }

  async #drain(workspaceId: string, run: { again: boolean }): Promise<void> {
    try {
      do {
        run.again = false;
        await this.read(workspaceId);
      } while (run.again);
    } finally {
      // After a reset a newer run may own the slot: only this run's own entry goes.
      if (this.#runs.get(workspaceId) === run) this.#runs.delete(workspaceId);
    }
  }

  reset(): void {
    this.#runs.clear();
  }
}

// The read asked last wins: an answer older than one already applied is dropped.
export class LatestReadGate {
  #asked = 0;
  #applied = 0;

  ask(): number {
    this.#asked += 1;
    return this.#asked;
  }

  mayApply(ticket: number): boolean {
    if (ticket < this.#applied) return false;
    this.#applied = ticket;
    return true;
  }

  reset(): void {
    this.#asked = 0;
    this.#applied = 0;
  }
}
